import { type DtoProjectType } from '@/shared/constants';

import { isCaptionAsset } from '../../assets/assets';
import {
  isCaptionsItem,
  type CaptionsItem,
} from '../../items/captions/captions-item-type';
import { type EditorState, type TrackType } from '../types';
import {
  applyCaptionBoundaryTiming,
  canApplyCaptionBoundaryTiming,
} from './apply-caption-boundary-timing';

export type CaptionAdditionalDisplayTimeFailureReason =
  | 'invalid-value'
  | 'invalid-target'
  | 'locked'
  | 'collision';

export type CaptionAdditionalDisplayTimeResult =
  | {
      ok: true;
      state: EditorState;
      changedItemIds: string[];
    }
  | {
      ok: false;
      state: EditorState;
      reason: CaptionAdditionalDisplayTimeFailureReason;
    };

type CaptionDisplayPlan = {
  itemId: string;
  endFrame: number;
};

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

const getCaptionSpeechEndMs = (item: CaptionsItem, state: EditorState) => {
  const asset = state.undoableState.assets[item.assetId];
  if (!isCaptionAsset(asset) || asset.captions.length === 0) {
    return null;
  }

  const endTimes = asset.captions
    .map((caption) => caption.endMs)
    .filter(isFiniteNumber);

  if (endTimes.length === 0) {
    return null;
  }

  return Math.max(...endTimes);
};

const getTrackForItem = (
  tracks: TrackType[],
  itemId: string,
): TrackType | null => {
  const matchingTracks = tracks.filter((track) => track.items.includes(itemId));

  return matchingTracks.length === 1 ? matchingTracks[0]! : null;
};

const hasTrackCollision = ({
  state,
  track,
  item,
  endFrame,
}: {
  state: EditorState;
  track: TrackType;
  item: CaptionsItem;
  endFrame: number;
}): boolean => {
  return track.items.some((otherItemId) => {
    if (otherItemId === item.id) return false;

    const otherItem = state.undoableState.items[otherItemId];
    if (!otherItem) return false;

    const otherEnd = otherItem.from + otherItem.durationInFrames;
    return otherItem.from < endFrame && otherEnd > item.from;
  });
};

const failed = (
  state: EditorState,
  reason: CaptionAdditionalDisplayTimeFailureReason,
): CaptionAdditionalDisplayTimeResult => ({
  ok: false,
  state,
  reason,
});

export const getCaptionAdditionalDisplayTimeMs = ({
  state,
  itemId,
}: {
  state: EditorState;
  itemId: string;
}): number | null => {
  const item = state.undoableState.items[itemId];
  if (!isCaptionsItem(item)) return null;

  const speechEndMs = getCaptionSpeechEndMs(item, state);
  if (speechEndMs == null) return null;

  const asset = state.undoableState.assets[item.assetId];
  const itemEndMs = Math.round(
    ((item.from + item.durationInFrames) / state.undoableState.fps) * 1000,
  );
  const displayEndMs = Math.max(
    speechEndMs,
    itemEndMs,
    isCaptionAsset(asset) && isFiniteNumber(asset.videoEnd)
      ? asset.videoEnd
      : speechEndMs,
    isFiniteNumber(item.renderMeta?.videoEnd)
      ? item.renderMeta.videoEnd
      : speechEndMs,
  );

  return Math.max(0, displayEndMs - speechEndMs);
};

/**
 * 선택 자막의 발화 타이밍은 보존하고, 음성 종료 뒤 화면에 남는 시간만
 * 원자적으로 변경한다. 모든 대상의 잠금·충돌을 먼저 검사하므로 일부만
 * 변경된 상태는 반환하지 않는다.
 */
export const applyCaptionAdditionalDisplayTime = ({
  state,
  itemIds,
  additionalDisplaySeconds,
  projectType,
}: {
  state: EditorState;
  itemIds: readonly string[];
  additionalDisplaySeconds: number;
  projectType: DtoProjectType | null | undefined;
}): CaptionAdditionalDisplayTimeResult => {
  if (
    !Number.isFinite(additionalDisplaySeconds) ||
    additionalDisplaySeconds < 0 ||
    !Number.isFinite(state.undoableState.fps) ||
    state.undoableState.fps <= 0
  ) {
    return failed(state, 'invalid-value');
  }

  const uniqueItemIds = [...new Set(itemIds)];
  if (uniqueItemIds.length === 0) {
    return failed(state, 'invalid-target');
  }

  const additionalDisplayMs = Math.round(additionalDisplaySeconds * 1000);
  const plans: CaptionDisplayPlan[] = [];

  for (const itemId of uniqueItemIds) {
    const item = state.undoableState.items[itemId];
    if (!isCaptionsItem(item)) {
      return failed(state, 'invalid-target');
    }

    const asset = state.undoableState.assets[item.assetId];
    if (
      !isCaptionAsset(asset) ||
      !canApplyCaptionBoundaryTiming({ item, asset, projectType })
    ) {
      return failed(state, 'invalid-target');
    }

    const track = getTrackForItem(state.undoableState.tracks, item.id);
    if (!track) {
      return failed(state, 'invalid-target');
    }
    if (track.locked) {
      return failed(state, 'locked');
    }

    const speechEndMs = getCaptionSpeechEndMs(item, state);
    if (speechEndMs == null) {
      return failed(state, 'invalid-target');
    }

    const desiredEndMs = speechEndMs + additionalDisplayMs;
    const desiredEndFrame = Math.max(
      item.from + 1,
      Math.ceil((desiredEndMs / 1000) * state.undoableState.fps),
    );

    if (!Number.isSafeInteger(desiredEndFrame)) {
      return failed(state, 'invalid-value');
    }

    if (
      hasTrackCollision({
        state,
        track,
        item,
        endFrame: desiredEndFrame,
      })
    ) {
      return failed(state, 'collision');
    }

    plans.push({ itemId, endFrame: desiredEndFrame });
  }

  let nextState = state;
  const changedItemIds: string[] = [];

  for (const plan of plans) {
    const item = nextState.undoableState.items[plan.itemId];
    if (!isCaptionsItem(item)) {
      return failed(state, 'invalid-target');
    }

    const asset = nextState.undoableState.assets[item.assetId];
    const currentEndFrame = item.from + item.durationInFrames;
    const requestedEndMs = Math.round(
      (plan.endFrame / nextState.undoableState.fps) * 1000,
    );
    const hasMatchingDisplayRange =
      currentEndFrame === plan.endFrame &&
      isCaptionAsset(asset) &&
      asset.videoEnd === requestedEndMs &&
      (item.renderMeta == null || item.renderMeta.videoEnd === requestedEndMs);

    if (hasMatchingDisplayRange) continue;

    const appliedState = applyCaptionBoundaryTiming({
      state: nextState,
      item,
      asset,
      edge: 'right',
      framePosition: plan.endFrame,
      fps: nextState.undoableState.fps,
      projectType,
    });

    if (!appliedState) {
      return failed(state, 'invalid-target');
    }

    nextState = appliedState;
    changedItemIds.push(plan.itemId);
  }

  return {
    ok: true,
    state: nextState,
    changedItemIds,
  };
};
